import { db } from './firebase';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  query,
  Timestamp
} from 'firebase/firestore';

export type NotificationTemplate = {
  id: string;
  title: string;
  body: string;
  enabled: boolean;
  sortOrder?: number;
  createdAt?: Date;
  updatedAt?: Date;
};

const templatesCol = () => collection(db, 'config', 'notifications', 'templates');

function convert(docSnap: any): NotificationTemplate {
  const d = docSnap.data() || {};
  const createdAt = d.createdAt instanceof Timestamp ? d.createdAt.toDate() : undefined;
  const updatedAt = d.updatedAt instanceof Timestamp ? d.updatedAt.toDate() : undefined;
  return {
    id: docSnap.id,
    title: (d.title || '').toString(),
    body: (d.body || '').toString(),
    enabled: d.enabled === true,
    sortOrder: typeof d.sortOrder === 'number' ? d.sortOrder : undefined,
    createdAt,
    updatedAt
  };
}

const notificationTemplatesService = {
  // List templates ordered by sortOrder then title (best-effort: single orderBy on sortOrder)
  getTemplates: async (): Promise<NotificationTemplate[]> => {
    // If sortOrder is missing for some docs, Firestore will still return them; we'll stable-sort by title after
    const q = query(templatesCol(), orderBy('sortOrder', 'asc'));
    const snap = await getDocs(q);
    const items = snap.docs.map(convert);
    // Stable sort by sortOrder (undefined -> 0) then by title
    return items.sort((a, b) => {
      const soA = a.sortOrder ?? 0;
      const soB = b.sortOrder ?? 0;
      if (soA !== soB) return soA - soB;
      return a.title.localeCompare(b.title);
    });
  },

  createTemplate: async (data: { title: string; body: string; enabled?: boolean; sortOrder?: number }): Promise<string> => {
    const trimmedTitle = (data.title || '').trim();
    const trimmedBody = (data.body || '').trim();
    if (!trimmedTitle || !trimmedBody) {
      throw new Error('Titre et message sont requis');
    }
    const res = await addDoc(templatesCol(), {
      title: trimmedTitle,
      body: trimmedBody,
      enabled: data.enabled !== false,
      sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return res.id;
  },

  updateTemplate: async (id: string, data: Partial<Omit<NotificationTemplate, 'id'>>) => {
    const ref = doc(db, 'config', 'notifications', 'templates', id);
    const updates: any = { ...data, updatedAt: serverTimestamp() };
    // Clean undefined
    Object.keys(updates).forEach((k) => {
      if (updates[k] === undefined) delete updates[k];
    });
    await updateDoc(ref, updates);
  },

  deleteTemplate: async (id: string) => {
    const ref = doc(db, 'config', 'notifications', 'templates', id);
    await deleteDoc(ref);
  },
};

export default notificationTemplatesService;
